import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiFetch, apiJson } from '@/lib/api-client';

export type ContactMessageStatus = 'NEW' | 'READ' | 'ARCHIVED';

export interface ContactMessage {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  ipAddress: string | null;
  status: ContactMessageStatus;
  createdAt: string;
  updatedAt: string;
}

export interface ContactMessageList {
  messages: ContactMessage[];
  total: number;
  unreadCount: number;
}

const listKey = (status?: ContactMessageStatus) =>
  ['contact-messages', { status: status ?? 'all' }] as const;
const itemKey = (id: string) => ['contact-message', id] as const;

export function useContactMessagesUnreadCount() {
  return useQuery({
    // Keep under the 'contact-messages' prefix so mutation invalidations bubble here too.
    queryKey: ['contact-messages', 'unread-count'] as const,
    queryFn: () =>
      apiJson<ContactMessageList>('/api/admin/contact-messages?pageSize=1'),
    select: (data) => data.unreadCount,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useContactMessagesQuery(status?: ContactMessageStatus) {
  return useQuery({
    queryKey: listKey(status),
    queryFn: () => {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      params.set('pageSize', '100');
      return apiJson<ContactMessageList>(
        `/api/admin/contact-messages?${params.toString()}`,
      );
    },
  });
}

export function useContactMessageQuery(id: string | undefined) {
  return useQuery({
    queryKey: id ? itemKey(id) : ['contact-message', 'noop'],
    enabled: !!id,
    queryFn: async () => {
      // The API doesn't expose a single-message GET; fetch the list and find it.
      const list = await apiJson<ContactMessageList>(
        '/api/admin/contact-messages?pageSize=100',
      );
      const found = list.messages.find((m) => m.id === id);
      if (!found) throw new Error('Message not found');
      return found;
    },
  });
}

export function useUpdateContactMessageStatusMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: string;
      status: ContactMessageStatus;
    }) => {
      const res = await apiFetch(`/api/admin/contact-messages/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw await asError(res);
      return (await res.json()) as ContactMessage;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['contact-messages'] });
      queryClient.setQueryData(itemKey(data.id), data);
      toast.success(`Marked as ${data.status.toLowerCase()}`);
    },
    onError: (e) => toast.error(message(e, 'Failed to update message')),
  });
}

export function useDeleteContactMessageMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch(`/api/admin/contact-messages/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw await asError(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-messages'] });
      toast.success('Message deleted');
    },
    onError: (e) => toast.error(message(e, 'Failed to delete message')),
  });
}

async function asError(res: Response): Promise<Error> {
  const text = await res.text().catch(() => '');
  return new Error(`${res.status} ${res.statusText}${text ? `: ${text}` : ''}`);
}
function message(e: unknown, fallback: string) {
  return e instanceof Error ? e.message : fallback;
}
